import { EmptyTable, type EmptyTableProps } from '@components/EmptyTable'
import { SelectIcon } from '@components/SelectIcon'
import { LINE } from '@helpers/specialChars'
import {
	Table as MuiTable,
	TableBody as MuiTableBody,
	type TableBodyProps,
	TableCell as MuiTableCell,
	type TableCellProps as MuiTableCellProps,
	TableContainer as MuiTableContainer,
	type TableContainerProps,
	TableHead as MuiTableHead,
	type TableHeadProps,
	type TableProps,
	TableRow as MuiTableRow,
	type TableRowProps,
	Typography,
	useTheme,
} from '@mui/material'
import type { CSSProperties, ReactNode } from 'react'

type EnhandedTableContainerProps = TableContainerProps &
	EmptyTableProps & { showEmptyTable?: boolean }

const TableContainer = ({
	style,
	title,
	subtitle,
	ctaText,
	ctaCallback,
	secondaryAction,
	showEmptyTable,
	...props
}: EnhandedTableContainerProps) => {
	const { spaces } = useTheme()

	if (showEmptyTable) {
		return (
			<EmptyTable
				title={title}
				subtitle={subtitle}
				ctaText={ctaText}
				ctaCallback={ctaCallback}
				secondaryAction={secondaryAction}
			/>
		)
	}

	return (
		<MuiTableContainer style={{ borderRadius: spaces.sm, ...style }} {...props}>
			{props.children}
		</MuiTableContainer>
	)
}

const Table = (props: TableProps) => (
	<MuiTable {...props}>{props.children}</MuiTable>
)

const TableHead = ({ style, ...props }: TableHeadProps) => {
	const { palette } = useTheme()

	return (
		<MuiTableHead
			style={{
				backgroundColor: palette.borderColors.darkestGray,
				borderRadius: '12px 12px 0px 0px',
				borderBottom: `2px solid ${palette.backgroundColors.black}`,
				...style,
			}}
			{...props}
		>
			{props.children}
		</MuiTableHead>
	)
}

const TableBody = (props: TableBodyProps) => (
	<MuiTableBody {...props}>{props.children}</MuiTableBody>
)

const TableRow = (props: TableRowProps) => (
	<MuiTableRow {...props}>{props.children}</MuiTableRow>
)

interface ITableCellProps extends MuiTableCellProps {
	minWidth?: string
	isSelected?: boolean
}

const TableCell = ({
	style,
	minWidth,
	isSelected,
	...props
}: ITableCellProps) => {
	const { palette } = useTheme()

	return (
		<MuiTableCell
			style={{
				minWidth,
				background: '#262626',
				border: `1px solid ${palette.backgroundColors.opaque}`,
				...(isSelected && {
					background: 'rgba(68, 197, 113, 0.5)',
				}),
				...style,
			}}
			{...props}
		>
			{props.children}
		</MuiTableCell>
	)
}

const TableHeaderCell = ({ style, minWidth, ...props }: ITableCellProps) => {
	const { palette } = useTheme()
	return (
		<TableCell
			style={{
				minWidth,
				borderTop: 'none',
				borderBottom: `1px solid ${palette.borderColors.darkGray}`,
				background: '#393939',
				...style,
			}}
			{...props}
		>
			<Typography style={{ fontSize: '0.875rem', fontWeight: '600' }}>
				{props.children}
			</Typography>
		</TableCell>
	)
}

interface ITableCellTypographyProps extends ITableCellProps {
	children?: React.ReactNode
}

const TableCellTypography = ({
	children,
	minWidth,
	...props
}: ITableCellTypographyProps) => (
	<TableCell
		{...props}
		style={{ userSelect: 'none', minWidth, ...props.style }}
	>
		<Typography variant="body2">{children ?? LINE}</Typography>
	</TableCell>
)

export interface IEnhancedTableHeadCellProps extends ITableCellProps {
	id: string
	label: string
	sticky?: boolean
	sublabel?: string
	sortable?: boolean
	labelColor?: string
	tooltipLabel?: string | ReactNode
	selectAll?: ReactNode | undefined
}

type TConditionalProps =
	| {
			isSelectable?: true
			isSelected: boolean
			selectTooltip?: string
			onSelectAllClick: () => void
			selectableCellsLabel?: never
	  }
	| {
			isSelectable?: false
			isSelected?: never
			selectTooltip?: never
			onSelectAllClick?: never
			selectableCellsLabel?: string
	  }

const EnhancedTableHead = ({
	isSelected,
	selectTooltip,
	style: _style,
	isSelectable = true,
	selectableCellsLabel,
	onSelectAllClick,
	...props
}: ITableCellProps & TConditionalProps) => {
	const borderBottom = '1px solid #d4d4d4'

	const style: CSSProperties = {
		..._style,
		borderBottom,
	}

	return (
		<TableHead>
			<TableRow>
				{isSelectable && onSelectAllClick && isSelected !== undefined ? (
					<TableHeaderCell
						{...props}
						style={{ ...style, maxWidth: '100px', width: '85px' }}
					>
						<SelectIcon
							label={selectTooltip}
							isSelected={isSelected}
							onClick={onSelectAllClick}
							condition={selectTooltip !== undefined}
						/>
					</TableHeaderCell>
				) : selectableCellsLabel ? (
					<TableCellTypography {...props} style={{ borderBottom }}>
						{selectableCellsLabel}
					</TableCellTypography>
				) : null}
			</TableRow>
		</TableHead>
	)
}

export {
	EnhancedTableHead,
	Table,
	TableBody,
	TableCell,
	TableCellTypography,
	TableContainer,
	TableHead,
	TableHeaderCell,
	TableRow,
}
